/**
 * Settings → Notifications → "Notify me about" (contracts NotifyKindPrefs): what can notify a person in a workspace, and
 * how that combines with a conversation's own level and mute. The server decides with `wantsAlert`, for push and for
 * the realtime banner alike, so every client version follows it. Precedence, Slack's: `none` silences a conversation;
 * mute lets only an explicit mention through; a conversation set to `all` or `mentions` wins over the switches, both
 * ways; `default` follows them. A message of several kinds alerts when any of them is on.
 */
import type { NotifyKind, NotifyKindPrefs, NotifyLevel, Role, WorkspaceFeatures } from "@bundle/contracts";
import { msg, type MessageDescriptor } from "../i18n.ts";
import { isFeatureEnabled } from "./features.ts";

/** Every switch, in the order of the screen. */
export const NOTIFY_KINDS: readonly NotifyKind[] = ["dm", "group", "mention", "broadcast", "thread", "channel", "huddle", "eventInvite", "eventReminder", "later", "memberJoined"];

/** Everything on, for everyone who hasn't changed a switch. */
export const DEFAULT_NOTIFY_KIND_PREFS: NotifyKindPrefs = { off: [] };

export function kindOn(prefs: NotifyKindPrefs | undefined, kind: NotifyKind): boolean {
  return prefs === undefined || !prefs.off.includes(kind);
}

/** The prefs with one switch changed: known kinds only, each once, in screen order. */
export function withKind(prefs: NotifyKindPrefs, kind: NotifyKind, on: boolean): NotifyKindPrefs {
  return { off: NOTIFY_KINDS.filter((k) => (k === kind ? !on : prefs.off.includes(k))) };
}

/** A conversation's own settings for one person. `explicit`: this message names them, or a group they're in. */
export type ConversationAlert = { level: NotifyLevel; muted: boolean; explicit?: boolean };

/** What "Mentions only" still lets through whatever the switches say, and what it stops. */
const MENTION_KINDS: readonly NotifyKind[] = ["mention", "broadcast"];
const NARROWED_KINDS: readonly NotifyKind[] = ["dm", "group", "channel"];

/** Whether something of these kinds in this conversation alerts this person. */
export function wantsAlert(prefs: NotifyKindPrefs | undefined, kinds: readonly NotifyKind[], c: ConversationAlert): boolean {
  if (c.level === "none") return false;
  if (c.muted && c.explicit !== true) return false;
  if (c.level === "all") return true;
  return kinds.some((k) => {
    if (c.level === "mentions") {
      if (MENTION_KINDS.includes(k)) return true;
      if (NARROWED_KINDS.includes(k)) return false;
    }
    return kindOn(prefs, k);
  });
}

/** The toast after changing a conversation's level, so a change made from a menu is seen to have happened. */
export function notifyLevelNotice(level: NotifyLevel): MessageDescriptor {
  return msg(level === "all" ? "channels.settings.notify.set.all" : level === "mentions" ? "channels.settings.notify.set.mentions" : level === "none" ? "channels.settings.notify.set.none" : "channels.settings.notify.set.default");
}

/** The toast after muting or unmuting a conversation. */
export function muteNotice(on: boolean): MessageDescriptor {
  return msg(on ? "channels.settings.muted" : "channels.settings.unmuted");
}

/** The kinds of a message reaching a recipient through mentions (DMs reach everyone in them). */
export function messageKinds(channelKind: string, explicit: boolean, broadcast: boolean): NotifyKind[] {
  const kinds: NotifyKind[] = [];
  if (channelKind === "dm") kinds.push("dm");
  if (channelKind === "group_dm") kinds.push("group");
  if (explicit) kinds.push("mention");
  if (broadcast || kinds.length === 0) kinds.push("broadcast");
  return kinds;
}

export type NotifyKindGroup = { group: "messages" | "calls" | "workspace"; kinds: NotifyKind[] };

/** The switches this person sees: areas the workspace has on, New members for those who get it (owners and admins). */
export function notifyKindGroups(features: WorkspaceFeatures | undefined, role: Role | undefined): NotifyKindGroup[] {
  const calendar: NotifyKind[] = isFeatureEnabled(features, "calendar") ? ["eventInvite", "eventReminder"] : [];
  const groups: NotifyKindGroup[] = [
    { group: "messages", kinds: isFeatureEnabled(features, "chat") ? ["dm", "group", "mention", "broadcast", "thread", "channel"] : [] },
    { group: "calls", kinds: [...(isFeatureEnabled(features, "huddles") ? (["huddle"] as const) : []), ...calendar, "later"] },
    { group: "workspace", kinds: role === "owner" || role === "admin" ? ["memberJoined"] : [] },
  ];
  return groups.filter((g) => g.kinds.length > 0);
}
